import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Agriculture as AgricultureIcon,
  Pets as PetsIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';
import { format } from 'date-fns';

interface ProductionPlan {
  id: string;
  plan_name: string;
  production_type: 'crop' | 'animal';
  farmer_id: string;
  farmer_name: string;
  variety_id: string;
  variety_name: string;
  variety_scientific_name?: string;
  planned_start_date: string;
  planned_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  status: 'draft' | 'active' | 'completed' | 'cancelled' | 'delayed';
  progress_percentage: number;
  description: string;
  created_at: string;
  updated_at: string;
  current_stage?: string;
  total_stages?: number;
  completed_stages?: number;
}

const ProductionPlans: React.FC = () => {
  const [plans, setPlans] = useState<ProductionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ProductionPlan | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  
  const navigate = useNavigate();

  // Fetch production plans
  const fetchProductionPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/production-plans`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setPlans(response.data);
      setError(null);
      toast.success('Production plans loaded');
    } catch (err) {
      console.error('Error fetching production plans:', err);
      setError('Failed to load production plans. Please try again later.');
      toast.error('Failed to load production plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductionPlans();
  }, []);

  // Handle delete confirmation
  const handleDeleteClick = (plan: ProductionPlan) => {
    setSelectedPlan(plan);
    setDeleteDialogOpen(true);
  };

  // Handle confirm delete
  const handleDeleteConfirm = async () => {
    if (!selectedPlan) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/production-plans/${selectedPlan.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Refresh the list after deletion
      fetchProductionPlans();
      toast.success('Production plan deleted successfully');
    } catch (err) {
      console.error('Error deleting production plan:', err);
      toast.error('Failed to delete production plan');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
    }
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, planId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPlanId(planId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPlanId(null);
  };

  // Handle edit
  const handleEdit = (id: string) => {
    navigate(`/production-plans/edit/${id}`);
    handleMenuClose();
  };

  // Handle view
  const handleView = (id: string) => {
    navigate(`/production-plans/${id}`);
    handleMenuClose();
  };

  // Handle row click
  const handleRowClick = (id: string) => {
    navigate(`/production-plans/${id}`);
  };

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter plans based on search term
  const filteredPlans = plans.filter(
    (plan) =>
      plan.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.farmer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.variety_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const paginatedPlans = filteredPlans.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'completed':
        return 'success';
      case 'delayed':
      case 'cancelled':
        return 'error';
      case 'draft':
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not started';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  // Get production type icon
  const getProductionTypeIcon = (type: string) => {
    return type === 'crop' ? (
      <AgricultureIcon fontSize="small" />
    ) : (
      <PetsIcon fontSize="small" />
    );
  };
  
  // Get progress text
  const getProgressText = (plan: ProductionPlan) => {
    if (plan.total_stages && plan.completed_stages !== undefined) {
      return `${plan.completed_stages} of ${plan.total_stages} stages`;
    }
    return `${plan.progress_percentage.toFixed(0)}% complete`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Production Plans
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage and track all production plans for farmers
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/production-plans/new')}
        >
          New Plan
        </Button>
      </Box>

      {/* Search and filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            sx={{ flex: 1, minWidth: 250 }}
            variant="outlined"
            placeholder="Search by plan name, farmer, or variety..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={searchTerm ? 'outlined' : 'contained'}
              onClick={() => setSearchTerm('')}
              disabled={!searchTerm}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => fetchProductionPlans()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Production plans table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan Details</TableCell>
                  <TableCell>Farmer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Timeline</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPlans.length > 0 ? (
                  paginatedPlans.map((plan) => (
                    <TableRow 
                      key={plan.id} 
                      hover
                      onClick={() => handleRowClick(plan.id)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={plan.production_type === 'crop' ? 'Crop' : 'Animal'}>
                            {getProductionTypeIcon(plan.production_type)}
                          </Tooltip>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {plan.plan_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {plan.variety_name}
                              {plan.variety_scientific_name && ` (${plan.variety_scientific_name})`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{plan.farmer_name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Farmer
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.production_type}
                          color={plan.production_type === 'crop' ? 'primary' : 'secondary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{formatDate(plan.planned_start_date)}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {plan.actual_start_date ? `Started: ${formatDate(plan.actual_start_date)}` : 'Not started'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
                          color={getStatusColor(plan.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 150 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="textSecondary">
                              {getProgressText(plan)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {Math.round(plan.progress_percentage)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={plan.progress_percentage}
                            sx={{ height: 8, borderRadius: 4 }}
                            color={
                              plan.progress_percentage < 30 ? 'error' :
                              plan.progress_percentage < 70 ? 'warning' : 'success'
                            }
                          />
                          {plan.current_stage && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                              Current: {plan.current_stage}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: '80px' }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, plan.id)}
                          aria-label="more"
                          aria-controls={`menu-${plan.id}`}
                          aria-haspopup="true"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No production plans found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPlans.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            if (selectedPlanId) {
              handleView(selectedPlanId);
            }
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            if (selectedPlanId) {
              handleEdit(selectedPlanId);
            }
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            if (selectedPlanId) {
              const planToDelete = plans.find(p => p.id === selectedPlanId);
              if (planToDelete) {
                handleDeleteClick(planToDelete);
              }
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error.main' }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Production Plan</DialogTitle>
        <DialogContent>
          {selectedPlan ? (
            <>
              <DialogContentText gutterBottom>
                Are you sure you want to delete the following production plan? This action cannot be undone.
              </DialogContentText>
              <Box sx={{ 
                p: 2, 
                mt: 2, 
                bgcolor: 'background.paper', 
                borderRadius: 1,
                borderLeft: '4px solid',
                borderColor: 'error.main'
              }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {selectedPlan.plan_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Farmer: {selectedPlan.farmer_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {selectedPlan.production_type.charAt(0).toUpperCase() + selectedPlan.production_type.slice(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {selectedPlan.status.charAt(0).toUpperCase() + selectedPlan.status.slice(1)}
                </Typography>
              </Box>
            </>
          ) : (
            <DialogContentText>
              Are you sure you want to delete this production plan? This action cannot be undone.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionPlans;
